import type { FetchAppBootstrapResult } from "../lib/api";
import type {
  ApprovalRequest,
  AuditEvent,
  BranchAccount,
  DeliveryArea,
  DemandGap,
  DoneForYouSetupRequest,
  DiscrepancyRecord,
  DiscoveryRunRecord,
  DiscoverySource,
  ExternalInquiryRecord,
  ExternalSupplierRecord,
  InvoiceRecord,
  LocalizationRule,
  MarketCoverageRecord,
  MessageThread,
  NetworkOperatingSnapshot,
  Order,
  OrderItem,
  PriceBenchmarkRecord,
  Product,
  QuoteRequest,
  RestaurantSupplierContactRecord,
  SourcingSprint,
  SupplierConversionTask,
  SupplierFulfillmentOrder,
  SupplierOrderRequest,
  SupplierSearchDemand,
  WorkspaceUser,
} from "../types";
import {
  readLocalCatalogProducts,
  readLocalConnectorRequests,
  readLocalDoneForYouSetupRequests,
  readLocalImportRuns,
  readLocalInvoices,
  readLocalOrderGuideItems,
  readLocalOrders,
  readLocalQuoteRequests,
  readLocalSupplierOrderRequests,
  readStoredJson,
} from "./workspaceStorage";
import type { ConnectorRequestRecord, ImportRunRecord } from "./workspaceStorage";

const BAM_PER_EUR = 1.95583;
const LOCAL_RESTAURANT_SUPPLIER_CONTACTS_KEY = "veleo.restaurantSupplierContacts";

type SuccessfulBootstrapResult = Extract<FetchAppBootstrapResult, { ok: true }>;

export interface WorkspaceBootstrapHydration {
  approvalRequests: ApprovalRequest[];
  auditEvents: AuditEvent[];
  branchAccounts: BranchAccount[];
  catalogProducts: Product[];
  connectorRequests: ConnectorRequestRecord[];
  deliveryAreas: DeliveryArea[];
  demandGaps: DemandGap[];
  doneForYouSetupRequests: DoneForYouSetupRequest[];
  discrepancies: DiscrepancyRecord[];
  discoveryRuns: DiscoveryRunRecord[];
  discoverySources: DiscoverySource[];
  draftItems: OrderItem[];
  externalInquiries: ExternalInquiryRecord[];
  externalSuppliers: ExternalSupplierRecord[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  importRuns: ImportRunRecord[];
  importStatus: string;
  invoices: InvoiceRecord[];
  localizationRules: LocalizationRule[];
  marketCoverageRecords: MarketCoverageRecord[];
  messageThreads: MessageThread[];
  networkActionState: string;
  networkSnapshot: NetworkOperatingSnapshot;
  orders: Order[];
  priceBenchmarks: PriceBenchmarkRecord[];
  preferredSupplierNames: string[];
  quoteRequests: QuoteRequest[];
  restaurantSupplierContacts: RestaurantSupplierContactRecord[];
  sourcingSprints: SourcingSprint[];
  supplierConversionTasks: SupplierConversionTask[];
  supplierOrderRequests: SupplierOrderRequest[];
  supplierSearchDemand: SupplierSearchDemand[];
  softwareConnectionStatus: string;
  workspaceUsers: WorkspaceUser[];
}

export interface LocalWorkspaceFallbackHydration {
  catalogProducts: Product[];
  connectorRequests: ConnectorRequestRecord[];
  doneForYouSetupRequests: DoneForYouSetupRequest[];
  draftItems: OrderItem[];
  fulfillmentOrders: SupplierFulfillmentOrder[];
  importRuns: ImportRunRecord[];
  invoices: InvoiceRecord[];
  orders: Order[];
  quoteRequests: QuoteRequest[];
  restaurantSupplierContacts: RestaurantSupplierContactRecord[];
  supplierOrderRequests: SupplierOrderRequest[];
}

function normalizeWorkspaceText(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function bamToEur(amountBam: number) {
  return amountBam / BAM_PER_EUR;
}

export function orderToFulfillmentOrder(order: Order): SupplierFulfillmentOrder {
  return {
    id: order.id,
    buyerOrderId: order.id,
    buyer: order.buyer,
    supplierName: order.supplier,
    market: order.city,
    itemCount: order.items.length,
    deliveryWindow: order.deliveryAt,
    valueEur: bamToEur(order.total),
    status: "needs_confirmation",
    nextAction: `Confirm ${order.items.length} line${order.items.length === 1 ? "" : "s"} and delivery slot`,
  };
}

export function localFulfillmentOrders() {
  return readLocalOrders().map((order) => orderToFulfillmentOrder(order));
}

export function productMergeKey(product: Product) {
  return `${normalizeWorkspaceText(product.supplier)}::${normalizeWorkspaceText(product.name)}`;
}

export function orderGuideMergeKey(item: OrderItem) {
  return `${normalizeWorkspaceText(item.supplier ?? "")}::${normalizeWorkspaceText(item.name)}`;
}

export function mergeByKey<T>(primary: T[], secondary: T[], keyFor: (item: T) => string) {
  const primaryKeys = new Set(primary.map(keyFor));

  return [...primary, ...secondary.filter((item) => !primaryKeys.has(keyFor(item)))];
}

export function mergeLocalOrders(remoteOrders: Order[]) {
  const localOrders = readLocalOrders();

  return [
    ...localOrders,
    ...remoteOrders.filter((order) => !localOrders.some((localOrder) => localOrder.id === order.id)),
  ];
}

export function mergeLocalFulfillmentOrders(remoteOrders: SupplierFulfillmentOrder[]) {
  const localOrders = localFulfillmentOrders();

  return [
    ...localOrders,
    ...remoteOrders.filter((order) => !localOrders.some((localOrder) => localOrder.id === order.id)),
  ];
}

export function mergeLocalCatalogProducts(remoteProducts: Product[]) {
  return mergeByKey(readLocalCatalogProducts(), remoteProducts, productMergeKey);
}

export function mergeLocalOrderGuideItems(remoteItems: OrderItem[]) {
  return mergeByKey(readLocalOrderGuideItems(), remoteItems, orderGuideMergeKey);
}

export function mergeLocalImportRuns(remoteRuns: ImportRunRecord[]) {
  return mergeByKey(readLocalImportRuns(), remoteRuns, (run) => run.batchId || run.id);
}

export function mergeLocalConnectorRequests(remoteRequests: ConnectorRequestRecord[]) {
  return mergeByKey(readLocalConnectorRequests(), remoteRequests, (request) => request.requestId || request.id);
}

export function mergeLocalDoneForYouSetupRequests(remoteRequests: DoneForYouSetupRequest[]) {
  return mergeByKey(readLocalDoneForYouSetupRequests(), remoteRequests, (request) => request.requestId || request.id);
}

export function mergeLocalQuoteRequests(remoteRequests: QuoteRequest[]) {
  return mergeByKey(readLocalQuoteRequests(), remoteRequests, (request) => request.id);
}

export function mergeLocalInvoices(remoteInvoices: InvoiceRecord[]) {
  return mergeByKey(readLocalInvoices(), remoteInvoices, (invoice) => invoice.id);
}

export function mergeLocalRestaurantSupplierContacts(remoteContacts: RestaurantSupplierContactRecord[]) {
  const localContacts = readStoredJson<RestaurantSupplierContactRecord[]>(
    LOCAL_RESTAURANT_SUPPLIER_CONTACTS_KEY,
    [],
  );

  return mergeByKey(localContacts, remoteContacts, (contact) =>
    `${normalizeWorkspaceText(contact.branchName)}::${normalizeWorkspaceText(contact.name)}`,
  );
}

export function mergeLocalSupplierOrderRequests(remoteRequests: SupplierOrderRequest[]) {
  return mergeByKey(readLocalSupplierOrderRequests(), remoteRequests, (request) => request.publicToken || request.id);
}

export function buildLocalWorkspaceFallbackHydration(): LocalWorkspaceFallbackHydration {
  return {
    catalogProducts: readLocalCatalogProducts(),
    connectorRequests: readLocalConnectorRequests(),
    doneForYouSetupRequests: readLocalDoneForYouSetupRequests(),
    draftItems: readLocalOrderGuideItems(),
    fulfillmentOrders: localFulfillmentOrders(),
    importRuns: readLocalImportRuns(),
    invoices: readLocalInvoices(),
    orders: readLocalOrders(),
    quoteRequests: readLocalQuoteRequests(),
    restaurantSupplierContacts: readStoredJson<RestaurantSupplierContactRecord[]>(
      LOCAL_RESTAURANT_SUPPLIER_CONTACTS_KEY,
      [],
    ),
    supplierOrderRequests: readLocalSupplierOrderRequests(),
  };
}

export function buildWorkspaceBootstrapHydration(
  result: SuccessfulBootstrapResult,
): WorkspaceBootstrapHydration {
  const connectorRequests = mergeLocalConnectorRequests(result.data.connectorRequests);
  const importCount = result.counts.imports ?? 0;
  const productCount = result.counts.products ?? 0;
  const orderCount = result.counts.orders ?? 0;

  return {
    approvalRequests: result.data.approvalRequests,
    auditEvents: result.data.auditEvents,
    branchAccounts: result.data.branchAccounts,
    catalogProducts: mergeLocalCatalogProducts(result.data.products),
    connectorRequests,
    deliveryAreas: result.data.deliveryAreas,
    demandGaps: result.data.demandGaps,
    doneForYouSetupRequests: mergeLocalDoneForYouSetupRequests(result.data.doneForYouSetupRequests),
    discrepancies: result.data.discrepancies,
    discoveryRuns: result.data.discoveryRuns,
    discoverySources: result.data.discoverySources,
    draftItems: mergeLocalOrderGuideItems(result.data.orderGuideItems),
    externalInquiries: result.data.externalInquiries,
    externalSuppliers: result.data.externalSuppliers,
    fulfillmentOrders: mergeLocalFulfillmentOrders(result.data.fulfillmentOrders),
    importRuns: mergeLocalImportRuns(result.data.importRuns),
    importStatus: importCount
      ? `${importCount} saved import batch${importCount === 1 ? "" : "es"} loaded`
      : "No saved import batches yet",
    invoices: mergeLocalInvoices(result.data.invoices),
    localizationRules: result.data.localizationRules,
    marketCoverageRecords: result.data.marketCoverageRecords,
    messageThreads: result.data.messageThreads,
    networkActionState: `Live data loaded: ${productCount} products / ${orderCount} orders`,
    networkSnapshot: result.data.networkSnapshot,
    orders: mergeLocalOrders(result.data.orders),
    priceBenchmarks: result.data.priceBenchmarks,
    preferredSupplierNames: result.data.preferredSupplierNames,
    quoteRequests: mergeLocalQuoteRequests(result.data.quoteRequests),
    restaurantSupplierContacts: mergeLocalRestaurantSupplierContacts(result.data.restaurantSupplierContacts),
    sourcingSprints: result.data.sourcingSprints,
    supplierConversionTasks: result.data.supplierConversionTasks,
    supplierOrderRequests: mergeLocalSupplierOrderRequests(result.data.supplierOrderRequests),
    supplierSearchDemand: result.data.supplierSearchDemand,
    softwareConnectionStatus: connectorRequests.length
      ? `${connectorRequests.length} software connection${connectorRequests.length === 1 ? "" : "s"} loaded`
      : "No software connections saved yet",
    workspaceUsers: result.data.workspaceUsers,
  };
}
